import { get, has, isEmpty } from 'lodash';
import {
  CalibrationData,
  CameraType,
  SingleFrameCalibrationData,
} from '../../team-apis/otterdam/calibration-data';
import { calibrationInputKey, Task } from '../../team-apis/otterdam/task';

export type ProjectionType =
  | CameraType
  | 'lut'
  | 'kannala_brandt'
  | 'brown_conrady';

export function getAngleToPixelUrl(outputName: string) {
  return `${outputName}_url_lut`;
}

export function getMetadataUrl(outputName: string) {
  return `${outputName}_url_metadata`;
}

export function getPixelToLightrayUrl(outputName: string) {
  return `${outputName}_url_lightray`;
}

export function getProjectionType(
  calibrationData: CalibrationData,
  task: Task,
  outputName: string,
): ProjectionType {
  const frameCalibrationData = getCalibrationDataForFrame(0, calibrationData);

  const distortionModel = get(
    frameCalibrationData,
    ['distortion', 'model'],
    'none',
  );
  if (distortionModel !== 'none') {
    return distortionModel;
  }

  const cameraType = get(frameCalibrationData, ['cameraType'], 'pinhole');
  const isPinhole = cameraType === 'pinhole';

  // Support old CDI tasks uploaded without cameraType
  if (isPinhole && isAngleToPixelLutConfigured(task, outputName)) {
    return 'lut';
  }

  const projectableCameraTypes: CameraType[] = [
    'cylinder',
    'deformed_cylindrical',
    'pinhole',
  ];

  if (projectableCameraTypes.includes(cameraType)) {
    return cameraType;
  }

  throw new Error('Not valid projection type found');
}

export function hasCalibrationData(task: Task, outputName: string) {
  return !isEmpty(task.data[`${outputName} ${calibrationInputKey}`]);
}

export function getCalibrationDataForFrame(
  frameNumber: number,
  calibrationData: CalibrationData,
): SingleFrameCalibrationData {
  return {
    distortion: getValue('distortion', frameNumber, calibrationData),
    focalLength: getValue('focalLength', frameNumber, calibrationData),
    position: getValue('position', frameNumber, calibrationData),
    principlePoint: getValue('principlePoint', frameNumber, calibrationData),
    rotation: getValue('rotation', frameNumber, calibrationData),
    cameraType: getValue('cameraType', frameNumber, calibrationData),
    timestamp: getValue('timestamp', frameNumber, calibrationData),
  };
}

function getValue<K extends keyof SingleFrameCalibrationData>(
  key: K,
  frameNumber: number,
  calibrationData: CalibrationData,
) {
  return (
    calibrationData.perFrame[frameNumber]?.[key] ?? calibrationData.static[key]!
  );
}

function isAngleToPixelLutConfigured(task: Task, videoOutputName: string) {
  return has(task, ['data', getAngleToPixelUrl(videoOutputName)]);
}
